import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('verifyEmail.invalidToken'));
      setLoading(false);
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || t('errors.generic'));
      }
    } catch (err) {
      setError(t('errors.network'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8">
        <div className="max-w-md w-full my-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border-2 border-gray-200">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('verifyEmail.verifying')}</h2>
            <p className="text-gray-600">
              {t('verifyEmail.pleaseWait')}
            </p>
          </div>
        </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8">
        <div className="max-w-md w-full my-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border-2 border-gray-200">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('verifyEmail.success')}</h2>
            <p className="text-gray-600 mb-6">
              {t('verifyEmail.successMessage')}
            </p>
            <p className="text-sm text-gray-500">
              {t('verifyEmail.redirecting')}
            </p>
          </div>
        </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8">
        <div className="max-w-md w-full my-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border-2 border-gray-200">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('verifyEmail.failed')}</h2>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <Link
              to="/login"
              className="inline-block bg-black text-white py-3 px-6 rounded-full hover:bg-gray-800 transition font-medium"
            >
              {t('verifyEmail.goToLogin')}
            </Link>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return null;
}
